/**
 * Copyright (c) 2026 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const PRESETS = [
  { label: '7d',  days: 7  },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y',  days: 365 },
];

// role:'agent' alone is not the Pro roster — Studio subscribers are also
// role:'agent' (with entitlements ['studio']) and never claim or match, so
// leaving them in would drag every adoption percentage down.
const AUDIENCES = [
  { key: 'pro', label: 'Pro agents' },
  { key: 'all', label: 'All agents' },
];

// 'inactive' IS cancelled — the cancel flow writes agentStatus 'inactive',
// there is no 'cancelled' value in the enum.
const COHORTS = [
  { key: 'all',      label: 'All',       status: 'all'      },
  { key: 'active',   label: 'Active',    status: 'active'   },
  { key: 'paused',   label: 'Paused',    status: 'paused'   },
  { key: 'inactive', label: 'Cancelled', status: 'inactive' },
  { key: 'compare',  label: 'Active vs Cancelled', status: null },
];

const COLOR_A = '#2563eb';
const COLOR_B = '#dc2626';

// Lifetime adoption. Each row is a count of DISTINCT AGENTS who have ever done
// the thing — not a count of events. Bars scale against the cohort's own
// roster, so length reads as "% of this cohort" and the two cohorts stay
// comparable even at very different sizes.
const ADOPTION_ROWS = [
  { key: 'loggedIn',        label: 'Logged in',        get: d => d?.adoption?.loggedIn        },
  { key: 'hasAssignedUser', label: 'Has a user',       get: d => d?.adoption?.hasAssignedUser },
  { key: 'claimed',         label: 'Claimed a user',   get: d => d?.adoption?.claimed         },
  { key: 'invited',         label: 'Invited a user',   get: d => d?.adoption?.invited         },
  { key: 'messaged',        label: 'Sent a message',   get: d => d?.adoption?.messaged        },
  { key: 'matched',         label: 'Sent a match',     get: d => d?.adoption?.matched         },
  { key: 'hasReferredUser', label: 'Referral signup',  get: d => d?.adoption?.hasReferredUser },
  { key: 'toppedUpPoints',  label: 'Topped up points', get: d => d?.adoption?.toppedUpPoints  },
  { key: 'createdVideo',    label: 'Made a video',     get: d => d?.adoption?.createdVideo    },
];

const WINDOW_ROWS = [
  { key: 'loggedIn', label: 'Logged in',      get: d => d?.window?.loggedIn },
  { key: 'claimed',  label: 'Claimed a user', get: d => d?.window?.claimed  },
  { key: 'invited',  label: 'Invited a user', get: d => d?.window?.invited  },
  { key: 'messaged', label: 'Sent a message', get: d => d?.window?.messaged },
  { key: 'matched',  label: 'Sent a match',   get: d => d?.window?.matched  },
];

const ROSTER_TILES = [
  { key: 'totalAgents', label: 'In Cohort',   get: d => d?.roster?.totalAgents },
  { key: 'active',      label: 'Active',      get: d => d?.roster?.active      },
  { key: 'paused',      label: 'Paused',      get: d => d?.roster?.paused      },
  { key: 'inactive',    label: 'Cancelled',   get: d => d?.roster?.inactive    },
  { key: 'pastDue',     label: 'Past Due',    get: d => d?.roster?.pastDue     },
  { key: 'beta',        label: 'Beta',        get: d => d?.roster?.beta        },
  { key: 'newInWindow', label: 'New Agents',  get: d => d?.roster?.newInWindow },
];

// perAgent: divide by roster size so cohorts of different sizes stay readable.
const VOLUME_TILES = [
  { key: 'claims',              label: 'Claims',           perAgent: true,  get: d => d?.volume?.claims              },
  { key: 'pointsSpent',         label: 'Points Spent',     perAgent: true,  get: d => d?.volume?.pointsSpent         },
  { key: 'refundedClaims',      label: 'Refunded Claims',  perAgent: false, get: d => d?.volume?.refundedClaims      },
  { key: 'invitationsSent',     label: 'Invites Sent',     perAgent: true,  get: d => d?.volume?.invitationsSent     },
  { key: 'invitationsAccepted', label: 'Invites Accepted', perAgent: false, get: d => d?.volume?.invitationsAccepted },
  { key: 'matchesSent',         label: 'Matches Sent',     perAgent: true,  get: d => d?.volume?.matchesSent         },
  { key: 'matchProperties',     label: 'Properties Sent',  perAgent: false, get: d => d?.volume?.matchProperties     },
  { key: 'agentMessages',       label: 'Agent Messages',   perAgent: true,  get: d => d?.volume?.agentMessages       },
  { key: 'referredSignups',     label: 'Referred Signups', perAgent: false, get: d => d?.volume?.referredSignups     },
  { key: 'videosMade',          label: 'Videos Made',      perAgent: false, get: d => d?.volume?.videosMade          },
];

const LIFETIME_TILES = [
  { key: 'claims',            label: 'Claims',             perAgent: true,  get: d => d?.lifetime?.claims            },
  { key: 'pointsSpent',       label: 'Points Spent',       perAgent: true,  get: d => d?.lifetime?.pointsSpent       },
  { key: 'pointsOutstanding', label: 'Points Outstanding', perAgent: false, get: d => d?.lifetime?.pointsOutstanding },
];

const AgentMetricsPanel = () => {
  const [startDate, setStartDate] = useState(toISODate(daysAgo(30)));
  const [endDate, setEndDate]     = useState(toISODate(new Date()));
  const [audience, setAudience]   = useState('pro');
  const [cohort, setCohort]       = useState('all');

  // dataB is only populated in compare mode; everything below treats a null
  // dataB as "single cohort" and renders one bar instead of a pair.
  const [dataA, setDataA]     = useState(null);
  const [dataB, setDataB]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const compare = cohort === 'compare';
  const labelA  = compare ? 'Active' : (COHORTS.find(c => c.key === cohort)?.label || 'All');
  const labelB  = 'Cancelled';

  const fetchOne = useCallback(async (status) => {
    const token = AuthService.getToken();
    const params = new URLSearchParams({ start: startDate, end: endDate, audience });
    if (status && status !== 'all') params.set('status', status);

    const res = await fetch(`${API_URL}/admin/metrics/agents?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();

    if (!res.ok || !json.success) {
      throw new Error(json.error || `Agent metrics request failed (${res.status})`);
    }
    return json;
  }, [startDate, endDate, audience]);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      if (compare) {
        // Two calls rather than one endpoint returning both cohorts: the query
        // is already cohort-scoped end to end, so this needs no backend change
        // and the two results can't drift apart in shape.
        const [a, b] = await Promise.all([fetchOne('active'), fetchOne('inactive')]);
        setDataA(a);
        setDataB(b);
      } else {
        const a = await fetchOne(COHORTS.find(c => c.key === cohort)?.status);
        setDataA(a);
        setDataB(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load agent metrics.');
    } finally {
      setLoading(false);
    }
  }, [fetchOne, cohort, compare]);

  useEffect(() => {
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (days) => {
    setStartDate(toISODate(daysAgo(days)));
    setEndDate(toISODate(new Date()));
  };

  const rosterA = dataA?.roster?.totalAgents || 0;
  const rosterB = dataB?.roster?.totalAgents || 0;

  const skel = <span className="skeleton">000</span>;

  const fmt = (v) => {
    if (loading) return skel;
    if (v == null || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString();
  };

  const pct = (num, den) => {
    if (loading) return skel;
    if (num == null || !den) return '—';
    return `${Math.round((num / den) * 100)}%`;
  };

  const ratio = (num, den) => {
    if (loading) return skel;
    if (num == null || !den) return '—';
    return (num / den).toFixed(1);
  };

  // ---- Bars -------------------------------------------------------------
  const barCell = (count, denominator, color) => {
    const width = denominator > 0 && count != null
      ? Math.min(100, (count / denominator) * 100)
      : 0;
    return (
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${width}%`, background: color }} />
      </div>
    );
  };

  const barText = (count, denominator) => {
    if (loading) return skel;
    if (count == null) return '—';
    const p = denominator ? Math.round((count / denominator) * 100) : 0;
    return `${count.toLocaleString()} · ${p}%`;
  };

  const renderBars = (rows) => rows.map(row => {
    const a = row.get(dataA);
    const b = compare ? row.get(dataB) : null;

    return (
      <div key={row.key} className="bar-row">
        <div className="bar-label">{row.label}</div>
        <div className="bar-stack">
          {barCell(a, rosterA, COLOR_A)}
          {compare && barCell(b, rosterB, COLOR_B)}
        </div>
        <div className="bar-value">
          <div style={{ color: compare ? COLOR_A : '#111' }}>{barText(a, rosterA)}</div>
          {compare && (
            <div style={{ color: COLOR_B, marginTop: '4px' }}>{barText(b, rosterB)}</div>
          )}
        </div>
      </div>
    );
  });

  // ---- Tiles ------------------------------------------------------------
  const tileValue = (tile) => {
    const a = tile.get(dataA);
    const b = compare ? tile.get(dataB) : null;

    if (!compare) {
      return (
        <>
          <div className="mini-value">{fmt(a)}</div>
          {tile.perAgent && rosterA > 0 && (
            <div className="mini-sub">{ratio(a, rosterA)} per agent</div>
          )}
        </>
      );
    }

    return (
      <div className="cohort-split">
        <div>
          <div className="cohort-tag" style={{ color: COLOR_A }}>{labelA}</div>
          <div className="mini-value" style={{ fontSize: '18px' }}>{fmt(a)}</div>
          {tile.perAgent && <div className="mini-sub">{ratio(a, rosterA)} / agent</div>}
        </div>
        <div>
          <div className="cohort-tag" style={{ color: COLOR_B }}>{labelB}</div>
          <div className="mini-value" style={{ fontSize: '18px' }}>{fmt(b)}</div>
          {tile.perAgent && <div className="mini-sub">{ratio(b, rosterB)} / agent</div>}
        </div>
      </div>
    );
  };

  const renderTiles = (tiles, badge) => (
    <div className="mini-grid">
      {tiles.map(tile => (
        <div className="mini-tile" key={tile.key}>
          {badge && <span className="lifetime-badge">{badge}</span>}
          <div className="mini-label">{tile.label}</div>
          {tileValue(tile)}
        </div>
      ))}
    </div>
  );

  const legend = compare && (
    <div className="legend">
      <span><i style={{ background: COLOR_A }} /> {labelA} ({rosterA.toLocaleString()})</span>
      <span><i style={{ background: COLOR_B }} /> {labelB} ({rosterB.toLocaleString()})</span>
    </div>
  );

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* Agent Metrics — same Control Center design system as UserMetricsPanel.
           Kept as a local <style> block so the panel stays self-contained and
           can be dropped in without touching a shared stylesheet. */

        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          margin-bottom: 16px;
        }

        .admin-panel .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .admin-panel .card-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .admin-panel .card-note {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 600;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25);
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .caveat {
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #92600a;
          border-radius: 10px;
          padding: 10px 12px;
          font-size: 12px;
          line-height: 1.5;
          margin-bottom: 14px;
        }

        .admin-panel .field-label {
          display: block;
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .admin-panel .date-input {
          padding: 8px 10px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          font-size: 13px;
          color: #111;
          background: #fff;
          outline: none;
          font-family: inherit;
          min-width: 150px;
        }
        .admin-panel .date-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .admin-panel .btn {
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 13px;
          letter-spacing: 0.01em;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #6b7280;
          font-family: inherit;
        }
        .admin-panel .btn:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .admin-panel .btn:disabled { opacity: 0.6; cursor: default; }
        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }
        .admin-panel .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          color: #fff;
        }

        .admin-panel .preset-chip {
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }
        .admin-panel .preset-chip:hover { background: #f9fafb; color: #111; }

        .admin-panel .audience-chip {
          padding: 7px 14px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: inherit;
        }
        .admin-panel .audience-chip:hover { background: #f9fafb; color: #111; }
        .admin-panel .audience-chip.on {
          background: rgba(37, 99, 235, 0.08);
          border-color: rgba(37, 99, 235, 0.35);
          color: #1d4ed8;
        }
        .admin-panel .audience-chip .dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #e5e7eb;
        }
        .admin-panel .audience-chip.on .dot { background: #2563eb; }

        /* Cancelled cohort reads red, matching the bars and the cancelled
           account banner in the agent portal. */
        .admin-panel .audience-chip.on.danger {
          background: rgba(220, 38, 38, 0.08);
          border-color: rgba(220, 38, 38, 0.35);
          color: #9b2c2c;
        }
        .admin-panel .audience-chip.on.danger .dot { background: #dc2626; }
        .admin-panel .audience-chip.on.split {
          background: linear-gradient(90deg, rgba(37,99,235,0.10), rgba(220,38,38,0.10));
          border-color: #d1d5db;
          color: #111;
        }
        .admin-panel .audience-chip.on.split .dot {
          background: linear-gradient(90deg, #2563eb 50%, #dc2626 50%);
        }

        .admin-panel .legend {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 11px;
          font-weight: 700;
          color: #6b7280;
        }
        .admin-panel .legend span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .admin-panel .legend i {
          width: 10px; height: 10px; border-radius: 3px;
          display: inline-block;
        }

        .admin-panel .stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        @media (max-width: 720px) {
          .admin-panel .stat-grid { grid-template-columns: 1fr; }
        }
        .admin-panel .stat-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 12px;
          padding: 18px;
        }
        .admin-panel .stat-tile.primary {
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.06), rgba(29, 78, 216, 0.04));
          border-color: rgba(37, 99, 235, 0.18);
        }
        .admin-panel .stat-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 8px;
        }
        .admin-panel .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .admin-panel .stat-tile.primary .stat-value { color: #1d4ed8; }
        .admin-panel .stat-sub {
          font-size: 11px;
          color: #6b7280;
          margin-top: 6px;
        }
        .admin-panel .stat-compare {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }
        .admin-panel .stat-compare .stat-value { font-size: 26px; }

        .admin-panel .mini-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 10px;
        }
        .admin-panel .mini-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 10px;
          padding: 14px;
          position: relative;
        }
        .admin-panel .mini-tile .mini-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 6px;
        }
        .admin-panel .mini-tile .mini-value {
          font-size: 22px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.01em;
          line-height: 1.15;
        }
        .admin-panel .mini-tile .mini-sub {
          font-size: 10px;
          color: #9ca3af;
          font-weight: 600;
          margin-top: 3px;
        }
        .admin-panel .cohort-split {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .admin-panel .cohort-tag {
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          margin-bottom: 2px;
        }
        .admin-panel .mini-tile .lifetime-badge {
          position: absolute;
          top: 8px; right: 8px;
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          padding: 2px 5px;
        }

        .admin-panel .bar-row {
          display: grid;
          grid-template-columns: 160px 1fr 130px;
          gap: 12px;
          align-items: center;
          padding: 9px 0;
          border-bottom: 1px solid #f5f5f5;
        }
        .admin-panel .bar-row:last-child { border-bottom: none; }
        .admin-panel .bar-label {
          font-size: 13px;
          font-weight: 600;
          color: #111;
        }
        .admin-panel .bar-stack {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .admin-panel .bar-track {
          height: 8px;
          background: #f3f4f6;
          border-radius: 999px;
          overflow: hidden;
        }
        .admin-panel .bar-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.4s ease;
        }
        .admin-panel .bar-value {
          font-size: 12px;
          font-weight: 700;
          color: #111;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        .admin-panel .skeleton {
          background: linear-gradient(90deg, #f3f4f6 0%, #fafafa 50%, #f3f4f6 100%);
          background-size: 200% 100%;
          animation: skel 1.4s linear infinite;
          border-radius: 6px;
          color: transparent;
        }
        @keyframes skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{
          fontSize: '11px', fontWeight: '600',
          letterSpacing: '0.08em', color: '#888',
          textTransform: 'uppercase'
        }}>
          Agent Metrics
        </span>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {/* Controls */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Filters</h3>
          <button className="btn btn-primary" onClick={fetchMetrics} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
          <div>
            <label className="field-label">Start date</label>
            <input
              type="date"
              className="date-input"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="field-label">End date</label>
            <input
              type="date"
              className="date-input"
              value={endDate}
              min={startDate}
              max={toISODate(new Date())}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px', paddingBottom: '1px' }}>
            {PRESETS.map(p => (
              <button
                key={p.label}
                className="preset-chip"
                onClick={() => applyPreset(p.days)}
                type="button"
              >
                Last {p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="field-label">Cohort</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {COHORTS.map(opt => {
              const on = cohort === opt.key;
              const variant = opt.key === 'inactive' ? ' danger' : opt.key === 'compare' ? ' split' : '';
              return (
                <button
                  key={opt.key}
                  type="button"
                  className={`audience-chip ${on ? 'on' + variant : ''}`}
                  onClick={() => setCohort(opt.key)}
                >
                  <span className="dot" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="field-label">Audience</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {AUDIENCES.map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`audience-chip ${audience === opt.key ? 'on' : ''}`}
                onClick={() => setAudience(opt.key)}
              >
                <span className="dot" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '14px' }}>
          Cohort and audience changes need a Refresh. The date range drives Window Activity and Volume; Adoption and Lifetime are all-time and ignore it.
          Cancelled agents are stored as status 'inactive'. Pro excludes Studio-only subscribers, who are also role 'agent'.
        </div>
      </div>

      {compare && (
        <div className="caveat">
          <strong>Reading this comparison.</strong> Adoption is the honest half — it's
          lifetime and asks "did this agent ever do X", so a cancelled agent who never
          claimed still counts as never having claimed. Window Activity is the trap:
          cancelled agents are locked out, so their recent numbers approach zero no matter
          what they did while subscribed. To compare behaviour rather than confirm they
          left, set the date range to a period when both cohorts were still active.
        </div>
      )}

      {/* Headline */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Summary</h3>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            {legend}
            {dataA && (
              <span className="card-note">
                {dataA.windowDays} day window · {new Date(dataA.start).toLocaleDateString()} – {new Date(dataA.end).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        <div className="stat-grid">
          <div className="stat-tile primary">
            <div className="stat-label">Activated</div>
            {compare ? (
              <div className="stat-compare">
                <div>
                  <div className="cohort-tag" style={{ color: COLOR_A }}>{labelA}</div>
                  <div className="stat-value">{pct(dataA?.adoption?.activated, rosterA)}</div>
                  <div className="stat-sub">{fmt(dataA?.adoption?.activated)} agents</div>
                </div>
                <div>
                  <div className="cohort-tag" style={{ color: COLOR_B }}>{labelB}</div>
                  <div className="stat-value" style={{ color: COLOR_B }}>{pct(dataB?.adoption?.activated, rosterB)}</div>
                  <div className="stat-sub">{fmt(dataB?.adoption?.activated)} agents</div>
                </div>
              </div>
            ) : (
              <>
                <div className="stat-value">{fmt(dataA?.adoption?.activated)}</div>
                <div className="stat-sub">
                  Ever claimed, invited, messaged, or matched · {pct(dataA?.adoption?.activated, rosterA)} of cohort
                </div>
              </>
            )}
          </div>

          <div className="stat-tile">
            <div className="stat-label">Active This Window</div>
            {compare ? (
              <div className="stat-compare">
                <div>
                  <div className="cohort-tag" style={{ color: COLOR_A }}>{labelA}</div>
                  <div className="stat-value">{pct(dataA?.window?.activeAgents, rosterA)}</div>
                  <div className="stat-sub">{fmt(dataA?.window?.activeAgents)} agents</div>
                </div>
                <div>
                  <div className="cohort-tag" style={{ color: COLOR_B }}>{labelB}</div>
                  <div className="stat-value" style={{ color: COLOR_B }}>{pct(dataB?.window?.activeAgents, rosterB)}</div>
                  <div className="stat-sub">{fmt(dataB?.window?.activeAgents)} agents</div>
                </div>
              </div>
            ) : (
              <>
                <div className="stat-value">{fmt(dataA?.window?.activeAgents)}</div>
                <div className="stat-sub">Distinct agents doing any of those four in range</div>
              </>
            )}
          </div>

          <div className="stat-tile">
            <div className="stat-label">Cohort Size</div>
            {compare ? (
              <div className="stat-compare">
                <div>
                  <div className="cohort-tag" style={{ color: COLOR_A }}>{labelA}</div>
                  <div className="stat-value">{fmt(rosterA)}</div>
                </div>
                <div>
                  <div className="cohort-tag" style={{ color: COLOR_B }}>{labelB}</div>
                  <div className="stat-value" style={{ color: COLOR_B }}>{fmt(rosterB)}</div>
                </div>
              </div>
            ) : (
              <>
                <div className="stat-value">{fmt(rosterA)}</div>
                <div className="stat-sub">{fmt(dataA?.roster?.newInWindow)} added in this window</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Roster */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Roster</h3>
          <span className="card-note">
            Lifecycle breakdown ignores the cohort filter, so you can see what you're slicing
          </span>
        </div>
        {renderTiles(ROSTER_TILES.map(t => ({ ...t, perAgent: false })), null)}
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
          An "unknown" figure that doesn't add up means some agents have no agentStatus set — those drop out of every cohort slice until backfilled.
        </div>
      </div>

      {/* Adoption funnel */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Adoption · Agents who have ever done this</h3>
          {legend || <span className="card-note">Distinct agents, all-time · share of {rosterA.toLocaleString()} agents</span>}
        </div>

        {renderBars(ADOPTION_ROWS)}

        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
          Rows overlap — most agents appear in several. "Has a user" counts every assignment path (claim, invite auto-assign, referral, manual), so it runs ahead of "Claimed a user".
          {compare && ' Each bar is a share of its own cohort, so the two stay comparable at different sizes.'}
        </div>
      </div>

      {/* Window activity */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Window Activity · Agents active in range</h3>
          {legend || <span className="card-note">Distinct agents · share of {rosterA.toLocaleString()} agents</span>}
        </div>

        {renderBars(WINDOW_ROWS)}

        {compare && (
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
            Near-zero cancelled bars over a recent range confirm they left, nothing more. Move the range back to when they were subscribed for a real comparison.
          </div>
        )}
      </div>

      {/* Volume */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Volume in Window</h3>
          {legend}
        </div>
        {renderTiles(VOLUME_TILES, null)}
      </div>

      {/* Per-agent ratios */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Rates</h3>
          <span className="card-note">
            Divided by the agents who actually did it, not the whole cohort
          </span>
        </div>
        <div className="mini-grid">
          {[
            { key: 'claimsPer',   label: 'Claims / Claiming Agent',    num: d => d?.volume?.claims,        den: d => d?.window?.claimed,  kind: 'ratio' },
            { key: 'pointsPer',   label: 'Points / Claiming Agent',    num: d => d?.volume?.pointsSpent,   den: d => d?.window?.claimed,  kind: 'ratio' },
            { key: 'matchesPer',  label: 'Matches / Matching Agent',   num: d => d?.volume?.matchesSent,   den: d => d?.window?.matched,  kind: 'ratio' },
            { key: 'msgsPer',     label: 'Messages / Messaging Agent', num: d => d?.volume?.agentMessages, den: d => d?.window?.messaged, kind: 'ratio' },
            { key: 'inviteRate',  label: 'Invite Accept Rate',         num: d => d?.volume?.invitationsAccepted, den: d => d?.volume?.invitationsSent, kind: 'pct' },
            { key: 'refundRate',  label: 'Claim Refund Rate',          num: d => d?.volume?.refundedClaims,     den: d => d?.volume?.claims,          kind: 'pct' },
          ].map(r => {
            const calc = (d) => r.kind === 'pct' ? pct(r.num(d), r.den(d)) : ratio(r.num(d), r.den(d));
            return (
              <div className="mini-tile" key={r.key}>
                <div className="mini-label">{r.label}</div>
                {compare ? (
                  <div className="cohort-split">
                    <div>
                      <div className="cohort-tag" style={{ color: COLOR_A }}>{labelA}</div>
                      <div className="mini-value" style={{ fontSize: '18px' }}>{calc(dataA)}</div>
                    </div>
                    <div>
                      <div className="cohort-tag" style={{ color: COLOR_B }}>{labelB}</div>
                      <div className="mini-value" style={{ fontSize: '18px' }}>{calc(dataB)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="mini-value">{calc(dataA)}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Lifetime */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Lifetime</h3>
          {legend}
        </div>
        {renderTiles(LIFETIME_TILES, 'All-time')}
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
          Points Outstanding is the sum of every agent's current balance — points sold but not yet spent.
          {compare && ' On the cancelled cohort that is unspent points sitting on dead accounts.'}
        </div>
      </div>
    </div>
  );
};

export default AgentMetricsPanel;